import { api } from 'shared/api/client';
import { apiErrorText } from 'shared/api/error-message';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { UUID, CreateExtensionRequest, DocumentExtensionSchema, ExtensionConceptSummary, UpdateExtensionRequest } from './types';
import { taxonomyQueryKeys } from './taxonomy';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n } from 'shared/i18n';

export const extensionQueryKeys = {
  extensions: 'EXTENSIONS',
  schema: 'EXTENSION_SCHEMA',
} as const;

export function useExtensions(documentSuperId: number | undefined, taxonomyId: UUID | undefined): UseQueryResult<ExtensionConceptSummary[]> {
  return useQuery({
    queryKey: [extensionQueryKeys.extensions, documentSuperId, taxonomyId],

    queryFn: async () => {
      const res = await api.GET('/concepts/extensions', {
        params: { query: { documentSuperId: String(documentSuperId), taxonomyId: taxonomyId as string } },
      });
      return (res?.data as ExtensionConceptSummary[]) || [];
    },

    enabled: !!documentSuperId && !!taxonomyId,
  });
}

export function useDocumentExtensionSchema(documentSuperId: number | undefined): UseQueryResult<DocumentExtensionSchema> {
  return useQuery({
    queryKey: [extensionQueryKeys.schema, documentSuperId],

    queryFn: async () => {
      const res = await api.GET('/document-taxonomy-config/{documentSuperId}/extension-schema', {
        params: { path: { documentSuperId: String(documentSuperId) } },
      });
      return res?.data || { prefix: null, namespace: null };
    },

    enabled: !!documentSuperId,
  });
}

export interface UpdateExtensionSchemaInput {
  documentSuperId: number;
  prefix: string;
  namespace: string;
}

export function useUpdateDocumentExtensionSchema(): UseMutationResult<DocumentExtensionSchema, unknown, UpdateExtensionSchemaInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentSuperId, prefix, namespace }: UpdateExtensionSchemaInput) => {
      const res = await api.PUT('/document-taxonomy-config/{documentSuperId}/extension-schema', {
        params: { path: { documentSuperId: String(documentSuperId) } },
        body: { prefix, namespace },
      });
      return res?.data;
    },

    onError: (error: unknown) => {
      const message = apiErrorText(error, i18n('tag:extension.schema.update.error'));
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:extension.schema.update.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [extensionQueryKeys.schema, variables.documentSuperId],
      });
    },
  });
}

export function useCreateExtension(): UseMutationResult<ExtensionConceptSummary, unknown, CreateExtensionRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateExtensionRequest) => {
      const res = await api.POST('/concepts/extensions', { body: input });
      return res?.data as ExtensionConceptSummary;
    },

    onError: (error: unknown) => {
      const message = apiErrorText(error, i18n('tag:extension.create.error'));
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:extension.create.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId],
      });
    },
  });
}

export function useUpdateExtension(): UseMutationResult<{ conceptId: UUID }, unknown, UpdateExtensionRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateExtensionRequest) => {
      const res = await api.PUT('/concepts/extensions/{id}', {
        params: { path: { id: input.conceptId }, query: { documentSuperId: String(input.documentSuperId) } },
        body: {
          name: input.name,
          parentMemberConceptId: input.parentMemberConceptId,
          description: input.description,
          labels: input.labels,
          widerAnchorConceptId: input.widerAnchorConceptId,
          narrowerAnchorConceptIds: input.narrowerAnchorConceptIds,
          conceptType: input.conceptType,
          summands: input.summands,
        },
      });
      return res?.data as { conceptId: UUID };
    },

    onError: (error: unknown) => {
      const message = apiErrorText(error, i18n('tag:extension.update.error'));
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:extension.update.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.dimensionsForConcept],
      });
    },
  });
}

export interface CreateExtensionMemberRequest {
  documentSuperId: number;
  taxonomyId: UUID;
  name: string;
  labels: Record<string, Record<string, string>>;
  axisConceptId: UUID;
  parentMemberConceptId: UUID;
  anchorMemberConceptId?: UUID;
  description?: string | null;
}

export function useCreateExtensionMember(): UseMutationResult<{ conceptId: UUID; name: string }, unknown, CreateExtensionMemberRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateExtensionMemberRequest) => {
      const res = await api.POST('/concepts/extensions', { body: { ...input, extensionShape: 'MEMBER' } });
      return res?.data as { conceptId: UUID; name: string };
    },

    onError: (error: unknown) => {
      const message = apiErrorText(error, i18n('tag:extension.create.error'));
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:extension.create.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.dimensionsForConcept],
      });
    },
  });
}

export interface DeleteExtensionInput {
  extensionId: UUID;
  documentSuperId: number;
  taxonomyId: UUID;
}

export function useDeleteExtension(): UseMutationResult<unknown, unknown, DeleteExtensionInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ extensionId, documentSuperId }: DeleteExtensionInput) => {
      const res = await api.DELETE('/concepts/extensions/{id}', {
        params: { path: { id: extensionId }, query: { documentSuperId: String(documentSuperId) } },
      });
      return res?.data;
    },

    onError: (error: unknown) => {
      const message = apiErrorText(error, i18n('tag:extension.delete.error'));
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:extension.delete.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.dimensionsForConcept],
      });
    },
  });
}
